<script setup lang="ts">
import { scrollToAnchor } from '@mop/shared/utils/util';

defineProps({
  error: {
    type: Object,
    default: null,
    loadErrorPage: () => {},
  },
});

const { $gtm, $storyblokLivePreview, $datadog, $resize, $mopI18n } = useNuxtApp();
const classNameRef = ref([$storyblokLivePreview.isEnabledInIframe ? 'wrapper__iframe-live-preview' : '']);
const {
  refreshCookiesLifespan,
  isCookieConsentRequired,
  isCookieConsentRequiredOnCurrentPage,
  hasTrackingCookiesBeenAccepted,
} = useMopPrivacy();
const overlay = useMopOverlay();
const { openCookieOverlay } = useMopCookieOverlayClient();
const { setPath } = useMopNavigation();
const route = useRoute();
const router = useRouter();
const errorRef = ref(false);
const requestEvent = useRequestEvent();
let stopCookieBannerWatcher: any;

function loadErrorPage() {
  errorRef.value = true;
  if (requestEvent?.node && !requestEvent.node?.res?.getHeader('location')) {
    requestEvent.node.res.statusCode = 404;
  }
}

router.beforeEach(() => {
  errorRef.value = false;
});

onMounted(() => {
  setPath(route?.path);
  const isGtmEnabled: boolean = $gtm.isEnabled();
  const { hasUserInteractedRef } = useMopGlobalEventsClient();
  const stopUserInteractedWatch: any = watch(hasUserInteractedRef, () => {
    isGtmEnabled && $gtm.initGtmScript();
    initOverlays();
    stopUserInteractedWatch();
  });
  handleDatadogSessionRecording();

  setTimeout(() => {
    watch(
      () => route.hash,
      () => {
        scrollToAnchor(route.hash);
      },
      { immediate: true },
    );
  }, 200);

  watch(
    $resize.viewportHeightRef,
    (viewportHeight) => {
      // used to calculate correct vh on ios devices, since vh is not working correctly
      document.body.style.setProperty('--vh', `${viewportHeight}px`);
    },
    { immediate: true },
  );

  watch(
    $resize.viewportOrientationRef,
    () => {
      // used to calculate correct vh on ios devices, since vh is not working correctly
      document.body.style.setProperty('--vh', `${$resize.viewportHeightRef.value}px`);
    },
    { immediate: true },
  );
});

function initOverlays() {
  if (route.query.disablecookiebanner) {
    return;
  }
  stopCookieBannerWatcher = watch(
    () => route.fullPath,
    async () => {
      if (!isCookieConsentRequiredOnCurrentPage()) {
        return;
      }

      if (isCookieConsentRequired()) {
        await openCookieOverlay(false, () => {
          setTimeout(checkOverlayState, 300);
          if (!isCookieConsentRequired()) {
            stopCookieBannerWatcher && stopCookieBannerWatcher();
          }
          handleDatadogSessionRecording();
        });
      } else {
        refreshCookiesLifespan();
        checkOverlayState();
        stopCookieBannerWatcher && stopCookieBannerWatcher();
      }
    },
    { immediate: true },
  );
}

function handleDatadogSessionRecording() {
  if (hasTrackingCookiesBeenAccepted()) {
    $datadog?.startSessionReplayRecording();
  } else {
    $datadog?.stopSessionReplayRecording();
  }
}

function checkOverlayState() {
  if (!isCookieConsentRequiredOnCurrentPage()) {
    return;
  }

  if (overlay?.activeOverlayRef?.value?.isOpen) {
    return;
  }

  /**
   * Our overlays are triggered by anchors ("#overlay-…"), but we don't want to safe them to the browsers history,
   * because that would reopen overlays on backwards navigation. In our "route change" middleware we take care for
   * that easily, but "route change" is not triggered when you enter the site—so we need the following additional
   * solution to handle this case. You can't use the "replace()" function by vue-router, because that would
   * trigger the "route change", which already touches the overlays, and would interfere with this solution.
   *
   * Using the "afterEach()" hook of the vue-router also doesn't work. You get an hydration issue due to SSR.
   */
  if (overlay.openFromUrlHash(route.hash?.substr(1))) {
    window.history.replaceState('', document.title, window.location.pathname + window.location.search);
  }
}
router.afterEach((to) => {
  setPath(to?.path);
});

const { getHeadObject } = useMopSeo();
const canonical = route.path;
const titleTemplate = `%s | ${$mopI18n.t('locale.brandName')}`;
const title: string = $mopI18n.t('locale.meta.title');
const description: string = $mopI18n.t('locale.meta.description');
const isIndexable = route.meta?.isNotIndexable !== true;
const seoObject = getHeadObject({
  title,
  description,
  isIndexable,
  canonical,
});

useHead({
  ...seoObject,
  titleTemplate,
  htmlAttrs: {
    lang: $mopI18n.lang,
  },
} as any);
</script>

<template>
  <div :class="['wrapper', ...classNameRef]">
    <MopHeader />
    <div class="wrapper__body lock-scroll-padding">
      <div v-show="!errorRef">
        <!-- eslint-disable-next-line -->
        <NuxtPage @page-not-found="loadErrorPage" />
      </div>
      <LazyMopNotFound v-if="errorRef" />
    </div>
    <MopFooterJobsStickyButton />
    <MopFooter class="lock-scroll-padding" />
    <MopOverlayManager group="global" show-shadow />
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  flex-direction: column;
}

.wrapper__body {
  min-height: calc(#{$ui-viewport-height} - #{$header-height});

  @include apply-upto(small) {
    min-height: calc(#{$ui-viewport-height} - #{$mobile-header-height});
  }

  @include apply-only-touch-device {
    min-height: calc(#{$ui-viewport-height} - #{$mobile-header-height});
  }
}
</style>
